import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import {
  Activity,
  AlertTriangle,
  Calendar,
  Clock,
  ExternalLink,
  MapPin,
  UserRound,
  Video,
} from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import { appointmentsAPI } from "@/lib/appointments-api";
import { useAuthStore } from "@/stores/authStore";
import { CONSOLE_PERMISSIONS } from "@/constants/permissions";
import { APPOINTMENT_CANCELLATION_CUTOFF_HOURS } from "@/constants/appointments";
import type {
  AppointmentDetails,
  StaffAppointment,
  StaffAppointmentAilment,
  StaffAppointmentAilments,
} from "@/types/appointments";
import type { ClinicSummary } from "@/types/clinics";

// ─── Normalized internal shape ────────────────────────────────────────────────

type MedicalItem = string | { name: string; [key: string]: unknown };

interface NormalizedAppointment {
  appointmentUUID: string;
  zarity_appointment_id: string;
  patient_name: string;
  patient_zarity_id: string;
  gender: string;
  age: string;
  service_name: string;
  status: string;
  start_time: string;
  end_time: string;
  event_duration: number | null;
  consultation_type: string;
  location: string;
  clinic?: ClinicSummary | null;
  doctor_name: string;
  doctor_designation: string;
  concerns: MedicalItem[];
  ailments: StaffAppointmentAilments;
  medications: MedicalItem[];
  last_visit_date: string | null;
  last_visit_doctor_name: string | null;
  last_visit_doctor_designation: string | null;
  prescription_url: string | null;
}

const EMPTY_AILMENTS: StaffAppointmentAilments = {
  confirmed: [],
  unconfirmed: [],
};

type AppointmentAilmentsInput = StaffAppointmentAilments | MedicalItem[] | null;

const isStructuredAilments = (
  value: AppointmentAilmentsInput | undefined
): value is StaffAppointmentAilments =>
  Boolean(value) &&
  !Array.isArray(value) &&
  Array.isArray(value.confirmed) &&
  Array.isArray(value.unconfirmed);

function normalizeAilments(
  items: AppointmentAilmentsInput | undefined
): StaffAppointmentAilments {
  if (isStructuredAilments(items)) {
    return items;
  }
  if (!items) {
    return EMPTY_AILMENTS;
  }

  if (import.meta.env.DEV) {
    console.warn(
      "Appointment details returned legacy string[] ailments. Remove this fallback after backend rollout.",
      items
    );
  }

  const unconfirmed: StaffAppointmentAilment[] = items.map((item, index) => {
    const name =
      typeof item === "string" ? item : item?.name || JSON.stringify(item);
    return {
      id: `legacy-${index}-${name}`,
      disease_id: null,
      name,
      is_custom: false,
      disease_status: "self_reported",
      source: "intake_form",
      source_record_id: null,
    };
  });
  return { confirmed: [], unconfirmed };
}

function fromStaffAppointment(appt: StaffAppointment): NormalizedAppointment {
  return {
    appointmentUUID: appt.id,
    zarity_appointment_id: appt.zarity_appointment_id,
    patient_name: appt.patient_name,
    patient_zarity_id: appt.patient_zarity_id,
    gender: appt.gender,
    age: appt.age,
    service_name: appt.service_name,
    status: appt.status,
    start_time: appt.start_time,
    end_time: appt.end_time,
    event_duration: appt.event_duration,
    consultation_type: appt.consultation_type,
    location: appt.location,
    clinic: appt.clinic ?? null,
    doctor_name: appt.doctor_name,
    doctor_designation: "",
    concerns: appt.concerns,
    ailments: normalizeAilments(appt.ailments),
    medications: appt.medications,
    last_visit_date: appt.last_visit_date || null,
    last_visit_doctor_name: appt.last_visit_doctor_name || null,
    last_visit_doctor_designation: appt.last_visit_doctor_designation || null,
    prescription_url: appt.prescription_url,
  };
}

function fromAppointmentDetails(
  appt: AppointmentDetails,
  appointmentId: string
): NormalizedAppointment {
  return {
    appointmentUUID: appointmentId,
    zarity_appointment_id: appt.zarity_appointment_id,
    patient_name: appt.patient_name,
    patient_zarity_id: appt.patient_zarity_id,
    gender: appt.gender,
    age: appt.age,
    service_name: appt.service_name,
    status: appt.appointment_status,
    start_time: appt.start_time,
    end_time: appt.end_time,
    event_duration: appt.service_event_duration
      ? parseInt(appt.service_event_duration, 10) || null
      : null,
    consultation_type: appt.consultation_type,
    location: appt.service_location,
    clinic: appt.clinic ?? null,
    doctor_name: appt.doctor_name,
    doctor_designation: appt.doctor_designation,
    concerns: appt.concerns,
    ailments: normalizeAilments(appt.ailments),
    medications: appt.medications,
    last_visit_date: appt.last_visit_date,
    last_visit_doctor_name: appt.last_visit_doctor_name,
    last_visit_doctor_designation: appt.last_visit_doctor_designation,
    prescription_url: appt.prescription_url,
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getStatusClassName = (status: string) => {
  switch (status.toLowerCase()) {
    case "completed":
      return "border-primary/20 bg-primary/10 text-primary";
    case "cancelled":
    case "invalidated":
      return "border-destructive/20 bg-destructive/10 text-destructive";
    case "confirmed":
      return "border-primary/20 bg-primary/10 text-primary";
    case "pending":
    case "soft_reserved":
    default:
      return "border-warning/30 bg-warning/10 text-warning-foreground";
  }
};

const toTitleCase = (value: string) =>
  value
    .replace(/_/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((s) => s[0]?.toUpperCase() + s.slice(1).toLowerCase())
    .join(" ");

const getMedicalItemName = (item: MedicalItem) =>
  typeof item === "string" ? item : item?.name || JSON.stringify(item);

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/** Consultation-type pill content. In-person (clinic present) always wins over
 *  the raw consultation_type string; teleconsult/legacy in-clinic behavior is
 *  unchanged when clinic is null. */
const getConsultationDisplay = (data: NormalizedAppointment) => {
  if (data.clinic) {
    return {
      icon: MapPin,
      label: "In-Person Consultation",
      clinicName: data.clinic.name,
    };
  }
  const isVideo = data.consultation_type.includes("Video");
  return {
    icon: isVideo ? Video : MapPin,
    label: isVideo ? data.consultation_type : data.location,
    clinicName: null as string | null,
  };
};

// ─── Shared sub-components ────────────────────────────────────────────────────

const SlotPill = ({
  icon: Icon,
  children,
}: {
  icon?: typeof Calendar;
  children: React.ReactNode;
}) => (
  <Badge
    variant="outline"
    className="flex items-center gap-1 rounded-full border-border bg-muted/50 px-2.5 py-0.5 z-caption text-foreground"
  >
    {Icon && <Icon className="h-3 w-3 shrink-0" />}
    {children}
  </Badge>
);

// ─── Clinician view ───────────────────────────────────────────────────────────
// Patient is the primary focus. Shows medical context and Start Consultation CTA.

const ClinicianView = ({
  data,
  formatDate,
  formatTime,
  formatLastVisitDate,
  onStartConsultation,
  isPast,
}: {
  data: NormalizedAppointment;
  formatDate: (s: string) => string;
  formatTime: (s: string) => string;
  formatLastVisitDate: (s: string | null) => string;
  onStartConsultation: () => void;
  isPast?: boolean;
}) => {
  const consultation = getConsultationDisplay(data);
  const confirmedAilments = data.ailments.confirmed;
  const unconfirmedAilments = data.ailments.unconfirmed;
  const hasEhrBrief =
    data.concerns.length > 0 ||
    confirmedAilments.length > 0 ||
    unconfirmedAilments.length > 0 ||
    data.medications.length > 0;
  const hasLastVisit = data.last_visit_date && data.last_visit_doctor_name;

  return (
    <>
      <div className="bg-muted/30 p-4 space-y-3 max-h-[70vh] overflow-y-auto">
        {/* Patient / Appointment Info */}
        <Card className="p-4 border-border">
          <div className="flex gap-2.5">
            <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center shrink-0">
              <span className="z-caption font-semibold text-muted-foreground">
                {getInitials(data.patient_name)}
              </span>
            </div>
            <div className="min-w-0 flex-1 space-y-1">
              {/* Row 1: Name + ZC | Service */}
              <div className="flex items-center justify-between gap-2">
                <DialogHeader className="space-y-0 text-left contents">
                  <div className="flex items-center gap-1.5 min-w-0 flex-1">
                    <DialogTitle className="z-body-md font-medium text-foreground truncate">
                      {data.patient_name}
                    </DialogTitle>
                    <Badge
                      variant="outline"
                      className="font-medium text-muted-foreground border-border shrink-0"
                    >
                      {data.patient_zarity_id}
                    </Badge>
                  </div>
                </DialogHeader>
                <Badge
                  variant="outline"
                  className="rounded-full border-primary/20 bg-primary/10 px-2.5 py-0.5 z-caption text-primary shrink-0"
                >
                  <Activity className="mr-1 h-3 w-3" />
                  {data.service_name}
                </Badge>
              </div>
              {/* Row 2: Gender · Age | ZAP */}
              <div className="flex items-center justify-between gap-2">
                <DialogDescription className="z-caption text-muted-foreground">
                  {data.gender} • Age {data.age}
                </DialogDescription>
                <Badge
                  variant="outline"
                  className="font-medium text-muted-foreground border-border tabular-nums shrink-0"
                >
                  {data.zarity_appointment_id}
                </Badge>
              </div>
              {/* Row 3: Date · Slot · Duration | Consultation type */}
              <div className="flex items-center justify-between gap-2">
                <span className="z-caption text-muted-foreground truncate">
                  {formatDate(data.start_time)} · {formatTime(data.start_time)}–
                  {formatTime(data.end_time)}
                  {data.event_duration ? ` · ${data.event_duration} min` : ""}
                </span>
                <div className="flex flex-col items-end gap-0.5 shrink-0">
                  <SlotPill icon={consultation.icon}>
                    {consultation.label}
                  </SlotPill>
                  {consultation.clinicName && (
                    <span className="z-caption text-muted-foreground truncate max-w-[180px]">
                      {consultation.clinicName}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* EHR Snapshot */}
        {hasEhrBrief && (
          <Card className="p-4 border-border">
            <p className="z-caption uppercase tracking-wide text-muted-foreground leading-none mb-0.5">
              EHR Snapshot
            </p>
            <div className="space-y-2.5">
              {data.concerns.length > 0 && (
                <div className="space-y-1">
                  <div className="z-caption text-muted-foreground">
                    Concerns
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {data.concerns.map((item, i) => (
                      <Badge
                        key={i}
                        variant="outline"
                        className="rounded-full border-border bg-muted/50 px-2.5 py-0.5 z-caption text-foreground"
                      >
                        {getMedicalItemName(item)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {confirmedAilments.length > 0 && (
                <div className="space-y-1">
                  <div className="z-caption text-muted-foreground">
                    Confirmed Diseases
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {confirmedAilments.map((item) => (
                      <Badge
                        key={item.id}
                        variant="outline"
                        className="rounded-full border-destructive/30 bg-destructive/10 px-2.5 py-0.5 z-caption text-destructive"
                      >
                        {item.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {unconfirmedAilments.length > 0 && (
                <div className="space-y-1">
                  <div className="z-caption text-muted-foreground">
                    Unconfirmed Diseases
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {unconfirmedAilments.map((item) => (
                      <Badge
                        key={item.id}
                        variant="outline"
                        className="rounded-full border-warning/30 bg-warning/10 px-2.5 py-0.5 z-caption text-warning-foreground"
                        title={toTitleCase(item.disease_status)}
                      >
                        {item.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {data.medications.length > 0 && (
                <div className="space-y-1">
                  <div className="z-caption text-muted-foreground">
                    Medications
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {data.medications.map((item, i) => (
                      <Badge
                        key={i}
                        variant="secondary"
                        className="rounded-full px-2.5 py-0.5 z-caption"
                      >
                        {getMedicalItemName(item)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Last Visits */}
        {hasLastVisit && (
          <Card className="p-4 border-border">
            <p className="z-caption uppercase tracking-wide text-muted-foreground leading-none mb-0.5">
              Last Visits
            </p>
            <div className="flex items-start justify-between gap-3">
              <div className="flex gap-2.5 min-w-0 flex-1">
                <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center shrink-0">
                  <UserRound className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="flex flex-col items-start gap-1 min-w-0">
                  <Badge
                    variant="outline"
                    className="font-medium text-foreground border-border"
                  >
                    {data.last_visit_doctor_name}
                  </Badge>
                  {data.last_visit_doctor_designation && (
                    <Badge
                      variant="outline"
                      className="font-medium text-muted-foreground border-border"
                    >
                      {data.last_visit_doctor_designation}
                    </Badge>
                  )}
                </div>
              </div>
              <div className="z-caption text-muted-foreground shrink-0 pt-1.5">
                {formatLastVisitDate(data.last_visit_date)}
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* CTA */}
      <div className="px-6 py-4 flex justify-end border-t border-border">
        <Button size="sm" onClick={onStartConsultation}>
          {isPast ? "Open Notes" : "Start Consultation"}
          <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
        </Button>
      </div>
    </>
  );
};

// ─── Admin view ───────────────────────────────────────────────────────────────
// Patient is still primary, but clinician name is surfaced and cancel is available.

const AdminView = ({
  data,
  formatDate,
  formatTime,
  formatLastVisitDate,
  canManageAppointments,
  onCancelClick,
}: {
  data: NormalizedAppointment;
  formatDate: (s: string) => string;
  formatTime: (s: string) => string;
  formatLastVisitDate: (s: string | null) => string;
  canManageAppointments: boolean;
  onCancelClick: () => void;
}) => {
  const consultation = getConsultationDisplay(data);
  const hasLastVisit = data.last_visit_date && data.last_visit_doctor_name;

  return (
    <>
      <div className="bg-muted/30 p-4 space-y-3 max-h-[70vh] overflow-y-auto">
        {/* Patient / Appointment Info */}
        <Card className="p-4 border-border">
          <div className="flex items-start justify-between gap-3">
            <div className="flex gap-2.5 min-w-0 flex-1">
              <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center shrink-0">
                <span className="z-caption font-semibold text-muted-foreground">
                  {getInitials(data.patient_name)}
                </span>
              </div>
              <div className="min-w-0 flex-1 space-y-1.5">
                <DialogHeader className="space-y-0.5 text-left">
                  <div className="flex items-center gap-2 flex-wrap">
                    <DialogTitle className="z-body-md font-medium text-foreground">
                      {data.patient_name}
                    </DialogTitle>
                    <Badge
                      variant="outline"
                      className="font-medium text-muted-foreground border-border shrink-0"
                    >
                      {data.patient_zarity_id}
                    </Badge>
                    <Badge
                      variant="outline"
                      className={cn(
                        "font-medium shrink-0",
                        getStatusClassName(data.status)
                      )}
                    >
                      {toTitleCase(data.status)}
                    </Badge>
                  </div>
                  <DialogDescription className="z-caption text-muted-foreground">
                    {data.gender} • Age {data.age}
                  </DialogDescription>
                </DialogHeader>
                <div className="flex flex-wrap gap-1.5">
                  <SlotPill icon={Calendar}>
                    {formatDate(data.start_time)}
                  </SlotPill>
                  <SlotPill icon={Clock}>
                    {formatTime(data.start_time)} – {formatTime(data.end_time)}
                    {data.event_duration ? ` · ${data.event_duration} min` : ""}
                  </SlotPill>
                </div>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1.5 shrink-0">
              <Badge
                variant="outline"
                className="rounded-full border-primary/20 bg-primary/10 px-2.5 py-0.5 z-caption text-primary"
              >
                <Activity className="mr-1 h-3 w-3" />
                {data.service_name}
              </Badge>
              <Badge
                variant="outline"
                className="font-medium text-muted-foreground border-border tabular-nums"
              >
                {data.zarity_appointment_id}
              </Badge>
              <SlotPill icon={consultation.icon}>{consultation.label}</SlotPill>
              {consultation.clinicName && (
                <span className="z-caption text-muted-foreground truncate max-w-[180px]">
                  {consultation.clinicName}
                </span>
              )}
            </div>
          </div>
        </Card>

        {/* Clinician */}
        {data.doctor_name && (
          <Card className="p-4 border-border">
            <p className="z-caption uppercase tracking-wide text-muted-foreground leading-none mb-0.5">
              Clinician
            </p>
            <div className="flex items-start gap-2.5">
              <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center shrink-0">
                <UserRound className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="flex flex-wrap gap-1.5">
                <Badge
                  variant="outline"
                  className="font-medium text-foreground border-border"
                >
                  {data.doctor_name}
                </Badge>
                {data.doctor_designation && (
                  <Badge
                    variant="outline"
                    className="font-medium text-muted-foreground border-border"
                  >
                    {data.doctor_designation}
                  </Badge>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Last Visits */}
        {hasLastVisit && (
          <Card className="p-4 border-border">
            <p className="z-caption uppercase tracking-wide text-muted-foreground leading-none mb-0.5">
              Last Visits
            </p>
            <div className="flex items-start justify-between gap-3">
              <div className="flex gap-2.5 min-w-0 flex-1">
                <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center shrink-0">
                  <UserRound className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="flex flex-col items-start gap-1 min-w-0">
                  <Badge
                    variant="outline"
                    className="font-medium text-foreground border-border"
                  >
                    {data.last_visit_doctor_name}
                  </Badge>
                  {data.last_visit_doctor_designation && (
                    <Badge
                      variant="outline"
                      className="font-medium text-muted-foreground border-border"
                    >
                      {data.last_visit_doctor_designation}
                    </Badge>
                  )}
                </div>
              </div>
              <div className="z-caption text-muted-foreground shrink-0 pt-1.5">
                {formatLastVisitDate(data.last_visit_date)}
              </div>
            </div>
          </Card>
        )}
      </div>

      {canManageAppointments && (
        <div className="px-6 py-4 flex justify-end border-t border-border">
          <Button
            size="sm"
            variant="outline"
            onClick={onCancelClick}
            className="text-destructive hover:text-destructive bg-destructive/10 hover:bg-destructive/20 border-destructive/20"
          >
            Cancel Appointment
          </Button>
        </div>
      )}
    </>
  );
};

// ─── Public component ─────────────────────────────────────────────────────────

export interface AppointmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAppointmentCancelled?: () => void;
  /** Pass a full StaffAppointment to skip the fetch (home dashboard). */
  appointment?: StaffAppointment | null;
  /** Pass an ID to fetch AppointmentDetails (appointments list). */
  appointmentId?: string | null;
  /** Renders the past-appointment variant (Open Notes instead of Start Consultation). */
  isPast?: boolean;
  /** Override clinician detection. Defaults to `appointment !== undefined`. */
  isClinician?: boolean;
  /** Override appointment-management capability. Defaults to auth permissions. */
  canManageAppointments?: boolean;
}

export const AppointmentDetailsDialog = ({
  open,
  onOpenChange,
  onAppointmentCancelled,
  appointment,
  appointmentId,
  isPast,
  isClinician: isClinicianProp,
  canManageAppointments: canManageAppointmentsProp,
}: AppointmentDialogProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { hasPermission } = useAuthStore();

  // View selection: passing a StaffAppointment object means the home dashboard
  // (clinician context). Passing an appointmentId means the appointments list
  // (admin/management context). Callers that open by id but know the user is a
  // clinician (e.g. the calendar) can override via the `isClinician` prop.
  const isClinician = isClinicianProp ?? appointment !== undefined;
  const canManageAppointments =
    canManageAppointmentsProp ??
    hasPermission(CONSOLE_PERMISSIONS.MANAGE_APPOINTMENTS);

  const [data, setData] = useState<NormalizedAppointment | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelConfirmText, setCancelConfirmText] = useState("");
  const [isCancelling, setIsCancelling] = useState(false);

  useEffect(() => {
    if (!open) {
      setData(null);
      return;
    }

    // Direct object — normalize immediately, no fetch
    if (appointment) {
      setData(fromStaffAppointment(appointment));
      return;
    }

    // Fetch by ID
    if (!appointmentId) return;

    const load = async () => {
      setIsLoading(true);
      try {
        const details =
          await appointmentsAPI.getAppointmentDetails(appointmentId);
        setData(fromAppointmentDetails(details, appointmentId));
      } catch {
        toast({
          title: "Error",
          description: "Failed to load appointment details",
          variant: "destructive",
        });
        onOpenChange(false);
      } finally {
        setIsLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, appointment?.id, appointmentId]);

  // Warn before the attempt only. The API decides, and its refusal text is
  // what we show — see constants/appointments.ts.
  const hoursUntilStart = (() => {
    if (!data?.start_time) return null;
    const startsAt = parseISO(data.start_time).getTime();
    return Number.isNaN(startsAt) ? null : (startsAt - Date.now()) / 3_600_000;
  })();
  const isInsideCancellationCutoff =
    hoursUntilStart !== null &&
    hoursUntilStart < APPOINTMENT_CANCELLATION_CUTOFF_HOURS;

  const handleConfirmCancel = async () => {
    if (!data || cancelConfirmText.toUpperCase() !== "CANCEL") return;
    if (!canManageAppointments) {
      toast({
        title: "Access Denied",
        description: "You do not have permission to cancel appointments.",
        variant: "destructive",
      });
      setShowCancelDialog(false);
      return;
    }

    setIsCancelling(true);
    try {
      await appointmentsAPI.cancelAppointment(data.appointmentUUID);
      toast({
        title: "Success",
        description: "Appointment cancelled successfully",
      });
      setShowCancelDialog(false);
      setCancelConfirmText("");
      onOpenChange(false);
      onAppointmentCancelled?.();
    } catch (error: unknown) {
      const err = error as {
        message?: string;
        details?: string;
        status?: number;
      };
      toast({
        title: err?.status === 403 ? "Access Denied" : "Error",
        description:
          err?.status === 403
            ? "Your permissions no longer allow appointment cancellation."
            : err?.message || err?.details || "Failed to cancel appointment",
        variant: "destructive",
      });
    } finally {
      setIsCancelling(false);
    }
  };

  const handleCloseCancelDialog = (nextOpen: boolean) => {
    setShowCancelDialog(nextOpen);
    if (!nextOpen) setCancelConfirmText("");
  };

  if (!open) return null;

  const formatTime = (s: string) => {
    try {
      return format(parseISO(s), "hh:mm a");
    } catch {
      return "—";
    }
  };
  const formatDate = (s: string) => {
    try {
      return format(parseISO(s), "EEE, MMM d, yyyy");
    } catch {
      return "—";
    }
  };
  const formatLastVisitDate = (s: string | null) => {
    if (!s) return "—";
    try {
      return format(parseISO(s), "MMM d, yyyy");
    } catch {
      return "—";
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md gap-0 overflow-hidden p-0 sm:rounded-2xl">
          {isLoading ? (
            <div className="flex items-center justify-center px-6 py-16 text-center">
              <div>
                <div className="z-body-md text-foreground">
                  Loading appointment...
                </div>
                <div className="z-secondary mt-1 text-muted-foreground">
                  Pulling patient and session details.
                </div>
              </div>
            </div>
          ) : data ? (
            isClinician ? (
              <ClinicianView
                data={data}
                formatDate={formatDate}
                formatTime={formatTime}
                formatLastVisitDate={formatLastVisitDate}
                isPast={isPast}
                onStartConsultation={() => {
                  onOpenChange(false);
                  navigate(`/consultation/${data.appointmentUUID}`, {
                    state: { prescriptionUrl: data.prescription_url },
                  });
                }}
              />
            ) : (
              <AdminView
                data={data}
                formatDate={formatDate}
                formatTime={formatTime}
                formatLastVisitDate={formatLastVisitDate}
                canManageAppointments={canManageAppointments}
                onCancelClick={() => setShowCancelDialog(true)}
              />
            )
          ) : (
            <div className="flex items-center justify-center px-6 py-16 text-center">
              <div>
                <div className="z-body-md text-foreground">
                  Appointment not available
                </div>
                <div className="z-secondary mt-1 text-muted-foreground">
                  The selected appointment could not be loaded.
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel confirmation */}
      <Dialog open={showCancelDialog} onOpenChange={handleCloseCancelDialog}>
        <DialogContent className="max-w-md gap-0 overflow-hidden p-0 sm:rounded-2xl">
          <div className="border-b border-border px-6 py-5">
            <DialogHeader className="space-y-1 text-left">
              <DialogTitle className="z-h2 text-foreground">
                Cancel appointment
              </DialogTitle>
              <DialogDescription className="z-secondary text-muted-foreground">
                This action cannot be undone. Type{" "}
                <span className="font-medium text-destructive">CANCEL</span> to
                confirm.
              </DialogDescription>
            </DialogHeader>
          </div>
          <div className="space-y-4 px-6 py-6">
            {isInsideCancellationCutoff ? (
              <Alert variant="destructive">
                <AlertTriangle />
                <AlertDescription>
                  {hoursUntilStart !== null && hoursUntilStart > 0
                    ? `This appointment starts in under ${APPOINTMENT_CANCELLATION_CUTOFF_HOURS} hours.`
                    : "This appointment has already started."}{" "}
                  Cancellations this close to the start time are refused. You
                  can still try; the reason will be shown.
                </AlertDescription>
              </Alert>
            ) : null}
            <Input
              placeholder="Type CANCEL here"
              value={cancelConfirmText}
              onChange={(e) => setCancelConfirmText(e.target.value)}
              className="h-11"
            />
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleCloseCancelDialog(false)}
              >
                Keep Appointment
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={handleConfirmCancel}
                disabled={
                  cancelConfirmText.toUpperCase() !== "CANCEL" || isCancelling
                }
              >
                {isCancelling ? "Cancelling..." : "Confirm Cancellation"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
